import React, { useEffect, useState } from "react";
import { WifiOff, CloudUpload, RefreshCw } from "lucide-react";
import useOnlineStatus from "@/hooks/useOnlineStatus";
import {
  getPendingCount,
  onOfflineChange,
  syncOutbox,
  isDetectedOffline,
} from "@/lib/offlineApi";

/**
 * Persistent banner shown when the user is offline OR has pending outbox
 * mutations awaiting sync.
 */
export default function OfflineIndicator() {
  const browserOnline = useOnlineStatus();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [detectedOff, setDetectedOff] = useState(false);
  const online = browserOnline && !detectedOff;

  const refresh = async () => {
    setPending(await getPendingCount());
    setDetectedOff(isDetectedOffline());
  };

  useEffect(() => {
    refresh();
    const off = onOfflineChange(refresh);
    const iv = setInterval(refresh, 4000);
    return () => {
      off();
      clearInterval(iv);
    };
  }, []);

  const handleSyncNow = async () => {
    if (!online) return;
    setSyncing(true);
    try {
      await syncOutbox();
    } finally {
      setSyncing(false);
      refresh();
    }
  };

  if (online && pending === 0) return null;

  return (
    <div
      data-testid="offline-indicator"
      className={`fixed inset-x-0 z-40 top-0 flex justify-center px-3 pointer-events-none`}
    >
      <div
        className={`mt-3 pointer-events-auto flex items-center gap-3 rounded-full px-4 py-2 shadow-neu text-sm font-medium ${
          !online
            ? "bg-[#3F4E5B] text-white"
            : "bg-[#FFF7E1] text-[#7A5B00] border border-[#F1D97F]"
        }`}
      >
        {!online ? (
          <>
            <WifiOff className="w-4 h-4" />
            <span data-testid="offline-status-text">Offline — changes will sync when reconnected</span>
            {pending > 0 && (
              <span
                className="ml-2 rounded-full bg-white/20 px-2 py-0.5 text-xs"
                data-testid="offline-pending-count"
              >
                {pending} pending
              </span>
            )}
          </>
        ) : (
          <>
            <CloudUpload className="w-4 h-4" />
            <span data-testid="offline-status-text">
              {pending} change{pending === 1 ? "" : "s"} to sync
            </span>
            <button
              type="button"
              onClick={handleSyncNow}
              disabled={syncing}
              data-testid="offline-sync-now"
              className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#7CB342] text-white px-3 py-1 text-xs disabled:opacity-60"
            >
              <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing" : "Sync now"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
